import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, UserLink, TableWrap, Th, Td } from '../components/ui/bits';
import { useRpc, useSel } from '../lib/queries';
import type { ComplianceResp } from '../lib/types';
import { supabase } from '../lib/supabase';
import { fmtDate, fmtDateTime } from '../lib/format';

export const CompliancePage: React.FC = () => {
  const { data: dels, error, isLoading } = useSel<any[]>(['deletion-requests'], () =>
    supabase.from('deletion_requests').select('*').order('requested_at', { ascending: false }));
  const { data: c, error: cErr } = useRpc<ComplianceResp>('admin_dash_compliance');

  if (isLoading) return <LoadingRow />;
  if (error) return <ErrorPanel error={error} label="deletion_requests" />;

  const pending = (dels ?? []).filter(d => d.status === 'pending');
  const shares = c?.shares ?? [];
  const oldest = pending.reduce<number>((m, d) => Math.max(m, Math.floor((Date.now() - new Date(d.requested_at).getTime()) / 86_400_000)), 0);

  return (
    <div>
      <PageHeader title="Compliance" subtitle="Account deletion requests with their legal clock, public share links, consent and phone verification." />

      {cErr && <div className="mb-3"><ErrorPanel error={cErr} label="admin_dash_compliance" /></div>}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <StatTile label="Deletion requests pending" value={pending.length} tone={pending.length ? (oldest > 21 ? 'bad' : 'warn') : 'good'}
          sub={pending.length ? `Oldest is ${oldest} days old; the limit is one month` : `${(dels ?? []).filter(d => d.status === 'completed').length} completed to date`}
          source="deletion_requests" />
        <StatTile label="Reports publicly shared" value={c ? c.shares_total.toLocaleString('en-GB') : 'Loading'} sub="Live public links" source="sessions with a share token, every row counted" />
        <StatTile label="Phones verified" value={c ? c.profiles.phone_verified.toLocaleString('en-GB') : 'Loading'}
          sub={c ? `${c.profiles.phone_override} admin overrides, ${c.profiles.phone_required} required to verify` : undefined} source="profiles, internal accounts excluded" />
        <StatTile label="Terms accepted" value={c ? c.profiles.tos_accepted.toLocaleString('en-GB') : 'Loading'}
          sub={c ? (c.tos_versions.map(v => `${v.version}: ${v.n.toLocaleString('en-GB')}`).join(', ') || 'No versions recorded') : undefined} source="profiles.tos_version" />
      </div>

      <Section title="Deletion requests" sub="UK GDPR allows one month from the request. Every request is listed." source="deletion_requests" className="mb-3">
        {(dels ?? []).length === 0 ? <EmptyNote>No requests ever</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Requester</Th><Th>Scope</Th><Th>Requested</Th><Th>Clock</Th><Th>Status</Th><Th></Th></tr></thead>
            <tbody>
              {(dels ?? []).map(d => {
                const age = Math.floor((Date.now() - new Date(d.requested_at).getTime()) / 86_400_000);
                return (
                  <tr key={d.id}>
                    <Td>
                      <span className="font-medium text-stone-900">{d.user_email_at_request ?? 'Unknown'}</span>
                      {d.user_id && <span className="ml-1.5"><UserLink id={d.user_id} /></span>}
                    </Td>
                    <Td>{d.scope}</Td>
                    <Td>{fmtDate(d.requested_at)}</Td>
                    <Td>
                      {d.status === 'pending'
                        ? <Badge tone={age > 21 ? 'rose' : age > 14 ? 'amber' : 'stone'}>Day {age} of 30</Badge>
                        : <span className="text-stone-400">Closed</span>}
                    </Td>
                    <Td>
                      <Badge tone={d.status === 'completed' ? 'emerald' : d.status === 'pending' ? 'rose' : 'stone'}>{d.status === 'completed' ? 'Completed' : d.status === 'pending' ? 'Pending' : d.status}</Badge>
                      {d.status === 'completed' && !d.confirmation_email_sent_at && <Badge tone="amber" className="ml-1">No confirmation sent</Badge>}
                    </Td>
                    <Td>
                      <Link to={`/compliance/deletions/${d.id}`} className="text-indigo-600 font-semibold text-[12px] inline-flex items-center gap-1">
                        {d.status === 'pending' ? 'Fulfil' : 'View'} <ArrowRight className="w-3 h-3" />
                      </Link>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
          </TableWrap>
        )}
      </Section>

      <div className="grid md:grid-cols-2 gap-3">
        <Section title="Publicly shared reports" sub={`Newest ${Math.min(100, c?.shares_total ?? 0)} of ${(c?.shares_total ?? 0).toLocaleString('en-GB')}. Links are permanent until revoked from the session page or the user’s compliance tab.`} source="sessions with a share token">
          {(shares ?? []).length === 0 ? <EmptyNote>Nothing shared</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Report</Th><Th>User</Th><Th>Shared</Th></tr></thead>
              <tbody>
                {(shares ?? []).map(s => (
                  <tr key={s.id}>
                    <Td><Link to={`/sessions/${s.id}`} className="text-indigo-600 hover:underline">{s.title || s.type}</Link></Td>
                    <Td><UserLink id={s.user_id} label={s.email} /></Td>
                    <Td className="whitespace-nowrap">{fmtDateTime(s.shared_at)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
        <Section title="PII hygiene" sub="What runs automatically and the one gap.">
          <ul className="text-[12.5px] text-stone-600 space-y-1.5 list-disc pl-4">
            <li>Demo sessions: PII redacted nightly at 04:20 by cron.</li>
            <li>Referral fraud PII: purged at 180 days, nightly at 04:35.</li>
            <li>Referral IP log: no retention policy of its own yet; the fraud purge covers its payload. Worth a policy decision.</li>
            <li>Per-user data export lives on the user page (Export data) for subject-access requests.</li>
          </ul>
        </Section>
      </div>
    </div>
  );
};
